import { IInteractionTranscriptionSegment } from '@/js/services/api/interaction/types';

/**
 * Average speaking pace, used to estimate a spoken duration for a voice segment
 * whose real one wasn't measured (segmentStart equal to segmentEnd).
 */
const SYNTHETIC_SECONDS_PER_CHARACTER = 0.06;

/**
 * Chat has no real timeline, so its "duration" is the total character count
 * segments get proportioned against. Voice uses the real audio length.
 */
export const getTranscriptionDuration = (
    segments: IInteractionTranscriptionSegment[],
    isVoiceInteraction: boolean,
    audioFileLength: number
): number => {
    if (isVoiceInteraction) {
        return audioFileLength;
    }

    return segments.reduce((total, segment) => total + segment.text.length, 0);
};

/**
 * A segmentStart equal to segmentEnd means no duration was measured, estimated
 * here from text length: chat splits the total character count proportionally,
 * voice keeps its real start and caps a missing end at the next segment's start.
 */
export const estimateSegmentDurations = (
    segments: IInteractionTranscriptionSegment[],
    duration: number,
    isVoiceInteraction: boolean
): IInteractionTranscriptionSegment[] => {
    if (segments.length === 0) {
        return segments;
    }

    if (!isVoiceInteraction) {
        const totalCharCount = segments.reduce((total, segment) => total + segment.text.length, 0);
        const durationPerChar = totalCharCount > 0 ? duration / totalCharCount : 0;

        let accumulatedCharCount = 0;

        return segments.map((segment) => {
            const segmentDuration = Math.floor(segment.text.length * durationPerChar);

            if (segment.segmentStart !== segment.segmentEnd) {
                accumulatedCharCount += segmentDuration;
                return segment;
            }

            const estimated = {
                ...segment,
                segmentStart: accumulatedCharCount,
                segmentEnd: accumulatedCharCount + segmentDuration,
            };
            accumulatedCharCount += segmentDuration;

            return estimated;
        });
    }

    return segments.map((segment, index) => {
        if (segment.segmentStart !== segment.segmentEnd) {
            return segment;
        }

        const nextStart = segments[index + 1]?.segmentStart ?? duration;
        const availableGap = Math.max(0, nextStart - segment.segmentStart);
        const estimatedDuration = Math.min(
            availableGap,
            segment.text.length * SYNTHETIC_SECONDS_PER_CHARACTER
        );

        return { ...segment, segmentEnd: segment.segmentStart + estimatedDuration };
    });
};
